import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'redux/store';
import { Modal, Title } from 'components';
import { Card, Col, Row, Badge } from 'react-bootstrap';
import { getAccountsTableAdmin } from 'redux/slices/Accounts';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { getStoreGroupsTable } from 'redux/slices/StoreGroups';
import { UserAccess } from 'models/freedomModels/enums/userAccess';
import { createSingleSelectFilter } from 'components/AgGrid/FilterComponents/Filters';
import { AgGridToolbar } from 'components/AgGrid/AgGridToolbar';
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react';
import { AdminUser } from 'models/dbModels/users/User';
import { ZTooltip } from 'components/ZTooltip';
import { ZButton } from 'components/ZButton';
import { ZIconType } from 'helpers/utility/iconMap';
import { ColDef } from 'ag-grid-community';
import { StoreGroup } from 'models/dbModels/storeGroups/StoreGroup';
import { PermissionsList } from 'models/dbModels/permissions/PermissionsList';
import { visiblePermissions, getPermissionOptions } from 'components/PermissionSelector';
import { ZSingleSelectDropdown } from 'components/ZSingleSelectDropdown';
import { PermissionEntries } from 'models/dbModels/permissions/PermissionEntries';
import { UsersApi } from 'apis/UsersApi';
import { showToast } from 'redux/slices/Toasts';
import { ZMultiSelectDropdown } from 'components/ZMultiSelectDropdown';

export function Users() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const users = useAppSelector((state: RootState) => state.account);
	const storeGroups = useAppSelector((state: RootState) => state.storeGroups);
	const [showBulkEdit, setShowBulkEdit] = useState(false);
	const [bulkEditLoading, setBulkEditLoading] = useState(false);
	const tableRef = useRef<AgGridReact>(null);
	const [selected, setSelected] = useState<number[]>([]);
	const [bulkOptions, setBulkOptions] = useState<{
		permissions: {key: keyof PermissionsList, property: keyof PermissionEntries, value: boolean}[];
	}>({
		permissions: [],
	});
	const [selectedPermission, setSelectedPermission] = useState<keyof PermissionsList | null>(null);
	const [selectedPermissionProperties, setSelectedPermissionProperties] = useState<(keyof PermissionEntries)[]>([]);
	const [selectedPermissionValue, setSelectedPermissionValue] = useState<boolean | null>(null);
	const [permissionOptions] = useState(getPermissionOptions());

	const columnsb: ColDef<AdminUser>[] = [
		{
			headerName: 'ID',
			field: 'id',
			sortable: true,
			flex: 0.4,
			cellRenderer: (row: CustomCellRendererProps) => <Link to={`/support/user/${row.data.id}`}>{row.value}</Link>,
			checkboxSelection: true,
			headerCheckboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
			suppressHeaderMenuButton: true,
		},
		{
			headerName: 'Given Name',
			field: 'givenName',
			sortable: true,
			sortIndex: 0,
			flex: 1,
			cellRenderer: (row: CustomCellRendererProps) => <Link to={`/support/user/${row.data.id}`}>{row.value}</Link>,
			suppressHeaderMenuButton: true,
			filter: "agTextColumnFilter",
		},
		{
			headerName: 'Family Name',
			field: 'familyName',
			sortable: true,
			sortIndex: 1,
			flex: 1,
			cellRenderer: (row: CustomCellRendererProps) => <Link to={`/support/user/${row.data.id}`}>{row.value}</Link>,
			suppressHeaderMenuButton: true,
			filter: "agTextColumnFilter",
		},
		{
			headerName: 'Email',
			field: 'email',
			sortable: true,
			flex: 1,
			cellRenderer: (row: CustomCellRendererProps) => <Link to={`/support/user/${row.data.id}`}>{row.value}</Link>,
			suppressHeaderMenuButton: true,
			filter: "agTextColumnFilter",
		},
		{
			headerName: "comments",
			field: "comments",
			sortable: true,
			flex: 2,
			cellRenderer: (row: CustomCellRendererProps) => <Link to={`/support/user/${row.data.id}`}>{row.value}</Link>,
			suppressHeaderMenuButton: true,
			filter: "agTextColumnFilter",
		},
		{
			headerName: 'Store Group',
			field: 'storeGroupId',
			filterValueGetter: (params) => {
				const storeGroup = storeGroups.table.find((sg: StoreGroup) => sg.id === params.data?.storeGroupId);
				return storeGroup ? storeGroup.name : "";
			},
			sortable: true,
			filter: "agTextColumnFilter",
			suppressHeaderMenuButton: true,
			flex: 1,
			valueGetter: (params) => {
				const storeGroup = storeGroups.table.find((sg: StoreGroup) => sg.id === params.data?.storeGroupId);
				return storeGroup ? storeGroup.name : "";
			},
		},
		{
			headerName: 'Products',
			field: 'access',
			sortable: true,
			flex: 1,
			filter: 'agSetColumnFilter',
			filterValueGetter: (params) => {
				const access = params.data?.access ?? 0;
				return (access & UserAccess.ZFreedom) > 0 ? "ZFreedom" : (access & UserAccess.ZPocket) > 0 ? "ZPocket" : "";
			},
			cellRenderer: (row: CustomCellRendererProps) => { 
				return (
					<div className="d-inline-flex gap-1">
						{(row.data.access & UserAccess.ZFreedom) > 0 && <Badge className="me-1 badge-info">Z Freedom</Badge>}
						{(row.data.access & UserAccess.ZPocket) > 0 && <Badge className="me-1 badge-success">Z Pocket</Badge>}
					</div>
				);
			},
			valueGetter: (params) => {
				let response = "";
				if (params.data) {
					if ((params.data.access & UserAccess.ZFreedom) > 0) {
						response += "ZFreedom ";
					}	
					if ((params.data.access & UserAccess.ZPocket) > 0) {
						response += "ZPocket";
					}
				}
				return response.trim();
			},
			suppressHeaderMenuButton: true,
		},
		{
			headerName: 'Status',
			field: 'status',
			sortable: true,
			flex: 1,
			cellRenderer: (row: CustomCellRendererProps) => {
				return (
					<>
						{row.data.status === "Suspended" && <Badge className="me-1 badge-outline-danger">Suspended</Badge>}
						{row.data.status === "Active" && <Badge className="me-1 badge-outline-primary">Active</Badge>}
					</>
				);
			},
			filter: "agSetColumnFilter",
			suppressHeaderMenuButton: true,
		},
	];
	
	useEffect(() => {
		dispatch(getStoreGroupsTable());
		dispatch(getAccountsTableAdmin());
	}, []);


	function bulkUpdate(users: AdminUser[]) {
		//Bulk update the selected users with the new updates
		const temp = JSON.parse(JSON.stringify(users)) as AdminUser[];
		setBulkEditLoading(true);
		temp.forEach((user) => {
			bulkOptions.permissions.forEach((permission) => {
				if (user.permissions[permission.key] != null) {
					user.permissions[permission.key][permission.property] = permission.value;
				}
			});
		});
		
		UsersApi.AdminUpdate(temp).then((res) => {
			if (res.data.data) {
				dispatch(getAccountsTableAdmin());
			} else {
				// display toast
				showToast({ value: res.data.errors.map((e) => e.message) });
			}
		}).finally(() => {						
			setBulkEditLoading(false);
			setShowBulkEdit(false);
			setBulkOptions({
				permissions: [],
			});
			tableRef.current?.api.refreshCells();
		});
	}

	return (
		<>
			<Title pageTitle="Users" parent="Support" />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<div className="page-container">

									<AgGridToolbar 
											search={true}
											className="my-1 px-2 pt-2"
											handleSubmit={(searchState, filterState) => {
												tableRef.current?.api.setGridOption(
													"quickFilterText",
													searchState?.keyword,
												  );
												tableRef.current?.api.onFilterChanged();

												let accessFilter: number | null = null;
												if (filterState?.zFreedomFilter === true) accessFilter = UserAccess.ZFreedom;
												if (filterState?.zPocketFilter === true) accessFilter = (accessFilter ?? 0) | UserAccess.ZPocket;

												if (filterState?.zFreedomFilter === false && filterState?.zPocketFilter === false) accessFilter = 0;

												if (accessFilter === null) {
													tableRef.current?.api.setFilterModel({});
												} else {
													tableRef.current?.api.setFilterModel({
														access: {
															filterType: 'number',
															type: 'equals',
															filter: accessFilter,
														},													
													})
												}
											}}
											searchProps={
												{totalRows: users.table.length, placeholder: "", hideContains: false}
											}
											filters={[
												createSingleSelectFilter({
													options: [true, false],
													display: (option) => { return option === null ? "" : option === true ? "Yes" : "No" },
													value: (option) => option,
													hasNoneOption: true,
													label: 'Z Freedom',
													filterName: "zFreedomFilter"
												}),
												createSingleSelectFilter({
													options: [true, false],
													display: (option) => { return option === null ? "" : option === true ? "Yes" : "No" },
													value: (option) => option,
													hasNoneOption: true,
													label: 'Z Pocket',
													filterName: "zPocketFilter"
												})
											]}
											children={(<div className="d-inline-flex gap-1">
			
			
												<ZTooltip text="Add">
													<ZButton buttonSize="md" icon={ZIconType.Add} variant="light" onClick={() => navigate("/support/user/new")} />
												</ZTooltip>

												<ZTooltip text="Bulk Edit">
													<ZButton
														buttonSize="md"
														icon={ZIconType.Edit}
														disabled={selected.length === 0}
														variant="light"
														onClick={() => {
															setShowBulkEdit(true);
															setBulkEditLoading(false);
														}}
													/>
												</ZTooltip>
											</div>												
											)}
										/> 

									<Row>
										<AgGridReact
											ref={tableRef}
											columnDefs={columnsb}
											rowData={users.table}
											getRowId={(p) => p.data.id}
											rowSelection="multiple"
											className={`ag-theme-quartz ag-theme-custom w-100 flex-grow`}
											containerStyle={{ height: "60dvh" }}
											suppressRowClickSelection
											rowMultiSelectWithClick
											groupAllowUnbalanced
											animateRows
											columnMenu='new'
											onSelectionChanged={(e) => {
												setSelected(e.api.getSelectedRows().map((r: AdminUser) => r.id));
											}}								
										/>
									</Row>
								</div>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>


			<Modal
				title="Bulk Edit"
				show={showBulkEdit}
				buttonType="primary"
				buttonText={bulkEditLoading ? "Saving..." : "Save"}
				disableButtons={bulkEditLoading}
				hideMainButton={bulkOptions.permissions.length === 0}
				buttonCloseText="Cancel"
				size='lg'
				handleSubmit={() => {
					const selectedRows = tableRef.current?.api.getSelectedRows();
					bulkUpdate(selectedRows as AdminUser[]);
				}}
				handleClose={() => {
					setShowBulkEdit(false)
					setBulkEditLoading(false);
					setBulkOptions({
						permissions: [],
					})
				}
				}
				description={
					<>
					<Row>
						<Col xs={11}>
						<Row>

							<Col xs={4}>
								<ZSingleSelectDropdown 
									dropdownIsDisabled={bulkEditLoading}
									options={permissionOptions} 
									optionKey={e => e?.key} 
									display={e => e?.display ?? ""} 
									value={permissionOptions.find(i => i.key === selectedPermission) ?? null}
									onSelectionChange={(e) => setSelectedPermission(e?.key ?? null)}								
									/>
							</Col>
							<Col xs={4}>
								<ZMultiSelectDropdown 
									dropdownIsDisabled={bulkEditLoading}
									options={selectedPermission != null ? visiblePermissions.find(i => i.key === selectedPermission)?.values ?? [] : []} 
									disabled={() => false}
									display={e => e?.substring(3) ?? ""}
									value={(e) => e}
									values={selectedPermissionProperties}
									onSelectionChange={(e) => setSelectedPermissionProperties(e)}			
									canClear={true}
									hasAllOption={true}					
									/>
							</Col>
							<Col xs={4}>
								<ZSingleSelectDropdown
									dropdownIsDisabled={bulkEditLoading}
									options={[true, false]}
									optionKey={e => e}
									display={e => e === null ? "" : e === true ? "Yes" : "No"}
									value={selectedPermissionValue}
									onSelectionChange={(e) => setSelectedPermissionValue(e as boolean)}
									/>
							</Col>
							</Row>
						</Col>

						<Col xs={1}>
							<ZButton
								buttonSize="md"
								icon={ZIconType.Add}
								disabled={bulkEditLoading || selectedPermission == null || selectedPermissionProperties.length === 0 || selectedPermissionValue == null}
								loading={bulkEditLoading}
								variant="light"
								onClick={() => {
									if (selectedPermission != null && selectedPermissionProperties.length > 0 && selectedPermissionValue != null) {
										let prev = {...bulkOptions};
										
										selectedPermissionProperties.forEach(permissionProperty => {
											prev = {
												...prev,												
												permissions: [
													...prev.permissions.filter((p) => p.key !== selectedPermission || p.property !== permissionProperty),
													{key: selectedPermission, property: permissionProperty, value: selectedPermissionValue},
												],
											};
										});
										setBulkOptions(prev);
										setSelectedPermission(null);
										setSelectedPermissionProperties([]);
										setSelectedPermissionValue(null);
									}
								}}
							/>
						</Col>
					</Row>
					<Row className="mt-2">
						<Col xs={12}>
							{bulkOptions.permissions.map((option, index) => {
								return (
									<div key={index} className="d-inline-flex gap-1">
										<span className='d-inline-flex me-1'>

										<ZTooltip text={`${permissionOptions.find(i => i.key === option.key)?.display} - ${option.property.substring(3)} - ${option.value}`}>
											<Badge className="badge-info">{`${permissionOptions.find(i => i.key === option.key)?.display} - ${option.property.substring(3)} - ${option.value}`}</Badge>
										</ZTooltip>
										<Badge className="badge-danger" style={{ cursor: "pointer" }}
												onClick={(e) => {
													e.stopPropagation();
													setBulkOptions((prev) => ({
														...prev,
														permissions: prev.permissions.filter((p) => p.key !== option.key || p.property !== option.property),
													}));
												}}>X</Badge>
										</span>
									</div>
								);
							})}
						</Col>
					</Row>
				</>
				}
			/>
		</>
	);
}